import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { LocationProvider } from './contexts/LocationContext';
import Login from './pages/Login';
import Home from './pages/Home';
import KioskDetail from './pages/KioskDetail';
import KioskQRCode from './pages/KioskQRCode';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderTracking from './pages/OrderTracking';
import Orders from './pages/Orders';
import Profile from './pages/Profile';
import SavedCards from './pages/SavedCards';
import MapView from './pages/MapView';
import Coupons from './pages/Coupons';
import Cashback from './pages/Cashback';
import BeachInfo from './pages/BeachInfo';
import Referral from './pages/Referral';
import Loyalty from './pages/Loyalty';
import ReviewOrder from './pages/ReviewOrder';
import PartnerDashboard from './pages/partner/PartnerDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import CityHallDashboard from './pages/admin/CityHallDashboard';
import BrandDashboard from './pages/brand/BrandDashboard';
import LoadingSpinner from './components/common/LoadingSpinner';
import ChatBot from './components/common/ChatBot';
import ReferralCapture from './components/common/ReferralCapture';
import PushPermissionPrompt from './components/common/PushPermissionPrompt';
import InstallPWAPrompt from './components/common/InstallPWAPrompt';
import { initPagBankSDK } from './services/pagbankSDK';

import './App.css';

const ProtectedRoute = ({ children, allowedAccountTypes = null }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><LoadingSpinner size="lg" /></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedAccountTypes && user && !allowedAccountTypes.includes(user.account_type)) return <Navigate to="/" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><LoadingSpinner size="lg" /></div>;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  return (
    <>
      <ReferralCapture />
      <Routes>
        <Route path="/client-dashboard/" element={<ProtectedRoute allowedAccountTypes={['business_owner', 'business_staff', 'admin']}><PartnerDashboard /></ProtectedRoute>} />
        <Route path="/admin/" element={<ProtectedRoute allowedAccountTypes={['admin']}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/cityhall/dashboard" element={<ProtectedRoute allowedAccountTypes={['admin']}><CityHallDashboard /></ProtectedRoute>} />
        <Route path="/brand/dashboard" element={<ProtectedRoute allowedAccountTypes={['admin']}><BrandDashboard /></ProtectedRoute>} />

        <Route path="/login" element={<Login />} />
        <Route path="/" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><Home /></ProtectedRoute>
            <ChatBot />
            <PushPermissionPrompt />
            <InstallPWAPrompt />
          </div>
        } />
        <Route path="/map" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><MapView /></ProtectedRoute>
          </div>
        } />
        <Route path="/kiosk/:id" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><KioskDetail /></ProtectedRoute>
          </div>
        } />
        <Route path="/kiosk/:id/qr" element={<ProtectedRoute><KioskQRCode /></ProtectedRoute>} />
        <Route path="/cart" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><Cart /></ProtectedRoute>
          </div>
        } />
        <Route path="/checkout" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><Checkout /></ProtectedRoute>
          </div>
        } />
        <Route path="/order/:id" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><OrderTracking /></ProtectedRoute>
          </div>
        } />
        <Route path="/orders" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><Orders /></ProtectedRoute>
          </div>
        } />
        <Route path="/profile" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><Profile /></ProtectedRoute>
          </div>
        } />
        <Route path="/saved-cards" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><SavedCards /></ProtectedRoute>
          </div>
        } />
        <Route path="/coupons" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><Coupons /></ProtectedRoute>
          </div>
        } />
        <Route path="/cashback" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><Cashback /></ProtectedRoute>
          </div>
        } />
        <Route path="/beach-info" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><BeachInfo /></ProtectedRoute>
          </div>
        } />
        <Route path="/referral" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><Referral /></ProtectedRoute>
          </div>
        } />
        <Route path="/loyalty" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><Loyalty /></ProtectedRoute>
          </div>
        } />
        <Route path="/review/:orderId" element={
          <div className="max-w-md mx-auto min-h-screen bg-background shadow-2xl relative">
            <ProtectedRoute><ReviewOrder /></ProtectedRoute>
          </div>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  useEffect(() => {
    // Initialize PagBank SDK on app start
    const settings = JSON.parse(localStorage.getItem('app_settings') || '{}');
    const publicKey = process.env.REACT_APP_PAGBANK_PUBLIC_KEY || settings.pagbank_public_key;
    const environment = process.env.REACT_APP_ENVIRONMENT === 'production' ? 'production' : 'sandbox';
    
    if (publicKey) {
      initPagBankSDK({ publicKey, environment });
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <LocationProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors closeButton />
          </LocationProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
